/**
 * CEO Command Center - Domain Store
 * 
 * Zustand store for managing business domains dynamically.
 * Supports CRUD operations for custom domains.
 * 
 * @module stores/domainStore
 * @version 10.2.0
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

// ============================================
// TYPES
// ============================================

export interface DomainConfig {
  id: string;
  name: string;
  fullName: string;
  description: string;
  icon: string;
  color: string;
  uploadLabel: string;
  isDefault?: boolean;  // Default domains cannot be deleted
  isActive: boolean;
  order: number;
  createdAt: string;
  updatedAt: string;
}

interface DomainState {
  // Data
  domains: DomainConfig[];
  
  // Actions
  addDomain: (domain: Omit<DomainConfig, 'id' | 'createdAt' | 'updatedAt' | 'order'>) => DomainConfig;
  updateDomain: (id: string, updates: Partial<DomainConfig>) => void;
  deleteDomain: (id: string) => void;
  reorderDomains: (ids: string[]) => void;
  toggleDomainActive: (id: string) => void;
  
  // Getters
  getDomain: (id: string) => DomainConfig | undefined;
  getActiveDomains: () => DomainConfig[];
  getDefaultDomains: () => DomainConfig[];
  getCustomDomains: () => DomainConfig[];
}

// ============================================
// DEFAULT DOMAINS
// ============================================

const DEFAULT_DOMAINS: DomainConfig[] = [
  {
    id: 'EMG',
    name: 'EMG',
    fullName: 'Executive Management Group',
    description: 'Leadership decisions, strategic planning, and company direction',
    icon: '👥',
    color: 'bg-purple-500',
    uploadLabel: 'Upload EMG Minutes',
    isDefault: true,
    isActive: true,
    order: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'PRODUCT',
    name: 'Product',
    fullName: 'Product Development',
    description: 'Product roadmap, features, and technical decisions',
    icon: '🔧',
    color: 'bg-blue-500',
    uploadLabel: 'Upload Product Notes',
    isDefault: true,
    isActive: true,
    order: 1,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'GROWTH',
    name: 'Growth',
    fullName: 'Sales & Growth',
    description: 'Sales strategy, marketing, and business development',
    icon: '📈',
    color: 'bg-green-500',
    uploadLabel: 'Upload Growth Notes',
    isDefault: true,
    isActive: true,
    order: 2,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'OPERATIONS',
    name: 'Operations',
    fullName: 'Operations',
    description: 'Day-to-day operations, processes, and efficiency',
    icon: '⚙️',
    color: 'bg-amber-500',
    uploadLabel: 'Upload Ops Notes',
    isDefault: true,
    isActive: true,
    order: 3,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'FINANCE',
    name: 'Finance',
    fullName: 'Finance & Legal',
    description: 'Financial planning, budgets, and legal matters',
    icon: '💵',
    color: 'bg-emerald-500',
    uploadLabel: 'Upload Finance Notes',
    isDefault: true,
    isActive: true,
    order: 4,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
];

// ============================================
// UTILITIES
// ============================================

const generateId = () => `domain_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// ============================================
// STORE
// ============================================

export const useDomainStore = create<DomainState>()(
  devtools(
    persist(
      (set, get) => ({
        domains: DEFAULT_DOMAINS,
        
        // Add domain
        addDomain: (domainData) => {
          const domains = get().domains;
          const maxOrder = Math.max(...domains.map(d => d.order), -1);
          
          const domain: DomainConfig = {
            id: generateId(),
            ...domainData,
            order: maxOrder + 1,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
          
          set(state => ({
            domains: [...state.domains, domain],
          }));
          
          return domain;
        },
        
        // Update domain
        updateDomain: (id, updates) => {
          set(state => ({
            domains: state.domains.map(domain =>
              domain.id === id
                ? { ...domain, ...updates, updatedAt: new Date().toISOString() }
                : domain
            ),
          }));
        },
        
        // Delete domain (only custom domains)
        deleteDomain: (id) => {
          const domain = get().getDomain(id);
          if (!domain) return;
          
          if (domain.isDefault) {
            // For default domains, just hide them (set isActive to false)
            set(state => ({
              domains: (state.domains || []).map(d => 
                d.id === id ? { ...d, isActive: false } : d
              ),
            }));
          } else {
            // For custom domains, actually remove them
            set(state => ({
              domains: (state.domains || []).filter(d => d.id !== id),
            }));
          }
        },
        
        // Reorder domains
        reorderDomains: (ids) => {
          set(state => ({
            domains: state.domains.map(domain => ({
              ...domain,
              order: ids.indexOf(domain.id),
            })),
          }));
        },
        
        // Toggle domain active
        toggleDomainActive: (id) => {
          set(state => ({
            domains: state.domains.map(domain =>
              domain.id === id
                ? { ...domain, isActive: !domain.isActive, updatedAt: new Date().toISOString() }
                : domain
            ),
          }));
        },
        
        // Get domain by id
        getDomain: (id) => {
          const domains = get().domains || [];
          return domains.find(d => d.id === id || d.id.toUpperCase() === id.toUpperCase());
        },
        
        // Get active domains
        getActiveDomains: () => {
          const domains = get().domains || [];
          return domains
            .filter(d => d.isActive)
            .sort((a, b) => a.order - b.order);
        },
        
        // Get default domains
        getDefaultDomains: () => {
          const domains = get().domains || [];
          return domains.filter(d => d.isDefault);
        },
        
        // Get custom domains
        getCustomDomains: () => {
          const domains = get().domains || [];
          return domains.filter(d => !d.isDefault);
        },
      }),
      {
        name: 'sentiance_domains',
        version: 1,
        merge: (persistedState: any, currentState: any) => ({
          ...currentState,
          ...persistedState,
          // Ensure domains is always an array, use defaults if empty
          domains: Array.isArray(persistedState?.domains) && persistedState.domains.length > 0 
            ? persistedState.domains 
            : currentState.domains,
        }),
      }
    ),
    { name: 'ceo-domain-store' }
  )
);

// ============================================
// SELECTORS
// ============================================

export const useActiveDomains = () => {
  return useDomainStore(state => state.getActiveDomains());
};

export const useDomain = (id: string) => {
  return useDomainStore(state => state.getDomain(id));
};

export const useCustomDomains = () => {
  return useDomainStore(state => state.getCustomDomains());
};
